/**
 * ============================================================================
 *  BOT NAME LIST — flexible data, not logic.
 * ============================================================================
 *
 *  Edit this array freely to add / remove / swap the names bots spawn with. The
 *  bot logic (src/core/world.ts) never hardcodes names — it just picks from
 *  whatever list it is given, so this file is the single place to change them.
 *
 *  The vibe is a real .io lobby: gamer handles, leetspeak, lowercase one-worders,
 *  internet/meme humour, plain first names, and a few deliberately silly ones —
 *  so bots read as indistinguishable from real players.
 *
 *  CURATION RULE (keep it this way when editing): family-friendly only. No
 *  profanity (including phonetic/hidden), slurs, sexual content, hate symbols,
 *  harassment, or names targeting real people. Edgy/cheeky in STYLE, never in
 *  content. The game skews young.
 *
 *  Selection is done through the world's seeded RNG (determinism preserved) and
 *  never repeats a name already in use by a live blob, nor the player's name.
 */
export const BOT_NAMES: readonly string[] = [
  // --- Gamer handles: xX wrappers ---
  'xX_Shadow_Xx', 'xX_Reaper_Xx', 'xX_Sniper_Xx', 'xX_Phantom_Xx', 'xX_Venom_Xx',
  'xX_Frost_Xx', 'xX_Blaze_Xx', 'xX_Nova_Xx', 'xX_Ghost_Xx', 'xX_Storm_Xx',
  'xX_Titan_Xx', 'xX_Raven_Xx', 'xX_Wolf_Xx', 'xX_Dragon_Xx', 'xX_Vortex_Xx',
  'xX_Ace_Xx', 'xX_Hunter_Xx', 'xX_Eclipse_Xx', 'xX_Saber_Xx', 'xX_Comet_Xx',

  // --- Gamer handles: leetspeak ---
  'Pr0Gamer', 'N00bSlayer', 'L33tMaster', 'H4xx0r', 'Sn1per', 'Gh0stByte',
  'M1dnight', 'Cyb3rPunk', 'Pix3lPusher', 'Z3roCool', 'Sh4dowStep', 'Fr0stByte',
  'Bl1tzKid', 'Qu1ckScope', 'V1perStrike', 'R3ktRunner', 'N1njaMode', 'Pwn3dYou',
  'Cr1tHit', 'D3stroyer', 'M4verick', 'T0xicMango', 'Gl1tchLord', 'B0ssByte',

  // --- Gamer handles: tryhard / FPS lingo ---
  'NoScopeNed', 'QuickScoper', '360NoScope', 'HeadshotHarry', 'ClutchKing',
  'AimBot3000', 'TryHardTodd', 'SweatLordSam', 'CamperCarl', 'RushBros',
  'GankGod', 'LagSwitchLou', 'PingMaster', 'FragMachine', 'OneTapWonder',
  'BackstabBilly', 'SpawnCamper', 'MetaSlave', 'BunnyHopper', 'WallBanger',
  'Sniperz', 'Slayerz', 'Reaperz', 'Hunterz', 'Gamerz', 'Legendz', 'Crusherz',
  'TheGOAT', 'BigBrainPlays', 'GodMode', 'Unkillable', 'MainCharacter',
  'FinalBossFred', 'SpeedDemon', 'ComboKing', 'NoLifer', 'KeyboardWarrior',

  // --- Lowercase one-worders ---
  'vortex', 'glitch', 'pixel', 'byte', 'echo', 'drift', 'frost', 'ember',
  'comet', 'nova', 'blaze', 'surge', 'pulse', 'flux', 'zephyr', 'onyx',
  'cobalt', 'cinder', 'dusk', 'dawn', 'mist', 'haze', 'volt', 'jolt',
  'spark', 'blip', 'fuzz', 'quartz', 'flint', 'gale', 'rune', 'sleet',
  'thorn', 'vapor', 'prism', 'neon', 'chrome', 'static', 'hollow', 'riptide',
  'talon', 'fang', 'shade', 'wisp', 'drizzle', 'gloom', 'flicker', 'glimmer',
  'noodle', 'waffle', 'pickle', 'gizmo', 'sprocket', 'widget', 'pretzel',

  // --- Internet / meme humour ---
  'stonks', 'doge', 'poggers', 'yeet', 'sus', 'amogus', 'sigmaGrindset',
  'gigachad', 'skibidi', 'ohio', 'npc_andy', 'basedDept', 'cringeLord',
  'bigYikes', 'oofMaster', 'bruhMoment', 'gg_ez', 'getRekt', 'noobtube',
  'tryhardThanos', 'sweatyBetty', 'copium', 'hopium', 'ratioKing', 'moai',
  'derpFace', 'lolcat', 'trollFace', 'ragequit', 'afk_forever', 'smhmyhead',
  'pogChamp', 'kekw', 'wholesome100', 'sadge', 'gigaBrain', 'smolBean',
  'chonkyBoi', 'vibeCheck', 'mainQuest', 'sideQuest', 'touchGrass', 'malding',
  'lowBattery', 'buffering', 'error404', 'ctrlAltDefeat', 'nullPointer',
  'segFault', 'rubberDucky', 'keyboardCat', 'snoozeButton', 'coffeeGremlin',
  'nightOwl', 'redditMoment', 'impostorVibes', 'emergencyMeeting', 'ventGuy',

  // --- Plain first names ---
  'Alex', 'Sam', 'Jordan', 'Riley', 'Casey', 'Max', 'Leo', 'Mia',
  'Liam', 'Noah', 'Emma', 'Ava', 'Jack', 'Ella', 'Finn', 'Ivy',
  'Kai', 'Luca', 'Nina', 'Owen', 'Ruby', 'Theo', 'Zoe', 'Mila',
  'Eli', 'Cole', 'Jade', 'Wren', 'Beau', 'Hazel', 'Milo', 'Iris',
  'Felix', 'Luna', 'Oscar', 'Nora', 'Ezra', 'Cleo', 'Jude', 'Sage',
  'Rex', 'Pip', 'Gus', 'Mabel', 'Otis', 'Della', 'Hugo', 'June',
  'Arlo', 'Bea', 'Dexter', 'Frankie', 'Goldie', 'Hank', 'Indie', 'Jasper',
  'Kira', 'Lola', 'Manny', 'Opal', 'Percy', 'Quinn', 'Remy', 'Stella',

  // --- Deliberately silly ---
  'BobTheBlob', 'SirEatsALot', 'NotABot', 'DefinitelyHuman', 'TotallyHuman',
  'RealPlayerHonest', 'BlobRoss', 'Blobzilla', 'CaptainOrb', 'SoupTime',
  'BananaKing', 'CheeseLord', 'PotatoGod', 'AngryToast', 'SleepyCat',
  'GrumpyGus', 'MrBubbles', 'SnackAttack', 'WaffleKnight', 'NoodleArms',
  'PicklePete', 'BeanMachine', 'TheBlobfather', 'CirclePrince', 'OrbWeaver',
  'GooGoblin', 'JellyBean', 'MarshmallowMan', 'DonutDan', 'MuffinMan',
  'PancakePat', 'TacoCat', 'NachoAverage', 'SirSpinsalot', 'WiggleWorm',
  'BoopSnoot', 'ZoomZoom', 'YeetMachine', 'ProcrastiNate', 'SnackGremlin',
  'TumbleWeed', 'WobbleBob', 'SquishSquash', 'BouncyBall', 'PixelPenguin',
  'CrumbCollector', 'LintRoller', 'SockPuppet', 'RubberChicken', 'PogoStick',
];
